// Public self-signup REST client (Phase 2). Mirrors intakeApi.ts: no
// JWT, no axios interceptor, just fetch() against the intake API
// Gateway's /api/signup/* routes. Used by the public /signup and
// /signup/verify pages; those routes live outside ProtectedRoute.
//
// The base URL is read exactly the way intakeApi.ts reads it, so both
// public flows share one origin config. Falls back to the inferred
// origin when unset so a dev-server proxy can route /api to API Gateway.

const SIGNUP_API_BASE = (
  import.meta.env.VITE_INTAKE_API_URL ||
  import.meta.env.VITE_API_BASE_URL ||
  ""
).replace(/\/$/, "");

export class SignupApiError extends Error {
  constructor(
    public status: number,
    message: string,
  ) {
    super(message);
    this.name = "SignupApiError";
  }
}

// Server response envelope shared by both endpoints. The `message` is
// always server-authored so the blind/enumeration-safe wording (and the
// password-policy text on verify) reaches the user verbatim; the UI must
// never substitute its own copy for these.
interface SignupEnvelope {
  status: string;
  message: string;
}

export interface SignupRequestInput {
  code: string;
  email: string;
  firstName: string;
  lastName: string;
  // Optional, unlike firstName/lastName -- a blank phone just means the
  // Salesperson picker's autofill (NewApplication.tsx) leaves that field
  // for the operator to fill in manually.
  phone: string;
  countryAlpha: string;
  stateAlpha: string;
}

// Blind success: the server returns this same shape whether or not the
// email already exists, so callers must render it as-is and never infer
// account existence from it.
export interface SignupRequestResult {
  status: "accepted";
  message: string;
}

export interface SignupVerifyInput {
  email: string;
  code: string;
  password: string;
}

export interface SignupVerifyResult {
  // "success" once the account is ACTIVE or PENDING_APPROVAL; the message
  // tells the user whether they must wait for approval.
  status: "success";
  message: string;
}

async function _request<T>(path: string, body: unknown): Promise<T> {
  const url = `${SIGNUP_API_BASE}${path}`;
  const response = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  let parsed: unknown = null;
  try {
    parsed = await response.json();
  } catch {
    // Non-JSON body (e.g., an API Gateway-level 502). Leave as null.
  }
  if (!response.ok) {
    const env = parsed as Partial<SignupEnvelope> | null;
    // Prefer the server message so blind/enumeration-safe and
    // password-policy wording stays intact; fall back only when the body
    // was empty or non-JSON.
    throw new SignupApiError(
      response.status,
      env?.message || `Request failed (HTTP ${response.status}). Please try again.`,
    );
  }
  return parsed as T;
}

/**
 * POST /api/signup/request. Starts a self-signup: the server emails a
 * verification link when the address is eligible. The 200 response is
 * BLIND (identical for new and already-registered emails), so the caller
 * shows one neutral confirmation and never reveals which case occurred.
 */
export function requestSignup(
  input: SignupRequestInput,
): Promise<SignupRequestResult> {
  return _request<SignupRequestResult>("/api/signup/request", input);
}

/**
 * POST /api/signup/verify. Consumes the emailed code and sets the
 * account password. On 200 the account is ACTIVE (or PENDING_APPROVAL if
 * the org gates new members); render the returned message so the user
 * learns whether approval is still pending.
 */
export function verifySignup(
  input: SignupVerifyInput,
): Promise<SignupVerifyResult> {
  return _request<SignupVerifyResult>("/api/signup/verify", input);
}
